import { useState, useEffect } from 'react';
import { Search, Filter, Clock, Star, Mail, Award, Briefcase, ShieldCheck, AlertTriangle } from 'lucide-react';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Input } from '@/components/ui/input';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Alert, AlertDescription } from '@/components/ui/alert';
import { useRFQs } from '@/hooks/useRFQs';
import { JOB_CATEGORIES, formatCurrency, JobCategory, VendorMatchScore } from '@/lib/types/job';
import { Link } from 'react-router-dom';
import { supabase } from '@/integrations/supabase/client';
import { DeadlineTypeBadge, DeadlineType } from '@/components/jobs/DeadlineTypeBadge';
import { MatchScoreDisplay } from '@/components/rfq/MatchScoreDisplay';
import { RFQProductSummary } from '@/components/rfq/RFQProductSummary';
import { useVendorVerificationStatus } from '@/hooks/useVendorVerificationStatus';
import { useAuth } from '@/lib/auth';
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from '@/components/ui/tooltip';

export default function RFQsPage() {
  const [search, setSearch] = useState('');
  const [selectedCategory, setSelectedCategory] = useState<JobCategory | ''>('');
  const [currentUserId, setCurrentUserId] = useState<string | null>(null);
  const [matchScores, setMatchScores] = useState<Record<string, VendorMatchScore>>({});
  const [matchScoresLoading, setMatchScoresLoading] = useState(false);
  
  const { user } = useAuth();
  const { isVerified, loading: verificationLoading } = useVendorVerificationStatus();
  const isVendor = user?.role === 'vendor';
  const showRestricted = isVendor && !isVerified;
  
  const { rfqs, loading, error } = useRFQs({
    search: search || undefined,
    category: selectedCategory || undefined,
    excludeUserBids: true
  });

  useEffect(() => {
    const getCurrentUser = async () => {
      const { data: { user } } = await supabase.auth.getUser();
      setCurrentUserId(user?.id || null);
    };
    getCurrentUser();
  }, []);

  // Helper to get obscured organization name for unverified vendors
  const getObscuredOrgName = (rfq: any): string => {
    const industry = rfq.job?.organization?.organization_profiles?.[0]?.industry;
    if (industry) {
      // Capitalize first letter of each word
      const formatted = industry
        .split('_')
        .map((word: string) => word.charAt(0).toUpperCase() + word.slice(1))
        .join(' ');
      return `${formatted} Business`;
    }
    return 'Private Business';
  };

  // Fetch match scores for all RFQs with criteria
  useEffect(() => {
    const fetchAllMatchScores = async () => {
      if (!currentUserId || rfqs.length === 0) return;
      
      setMatchScoresLoading(true);
      try {
        const scores: Record<string, VendorMatchScore> = {};
        
        // Fetch match scores for each RFQ that has targeting criteria
        await Promise.all(
          rfqs
            .filter((rfq: any) => rfq.rfq_targeting_criteria)
            .map(async (rfq: any) => {
              const { data, error } = await supabase
                .rpc('calculate_vendor_match_score' as any, {
                  p_vendor_user_id: currentUserId,
                  p_rfq_id: rfq.id
                } as any);
              
              if (!error && data) {
                scores[rfq.id] = data as any as VendorMatchScore;
              }
            })
        );
        
        setMatchScores(scores);
      } catch (error) {
        console.error('Error fetching match scores:', error);
      } finally {
        setMatchScoresLoading(false);
      }
    };
    
    fetchAllMatchScores();
  }, [rfqs, currentUserId]);

  const getTimeRemaining = (closesAt: string) => {
    const timeLeft = new Date(closesAt).getTime() - new Date().getTime();
    if (timeLeft <= 0) return 'Closed';
    
    const days = Math.floor(timeLeft / (1000 * 60 * 60 * 24));
    const hours = Math.floor((timeLeft % (1000 * 60 * 60 * 24)) / (1000 * 60 * 60));
    
    if (days > 0) return `${days}d ${hours}h left`;
    return `${hours}h left`;
  };

  const isUserInvited = (rfq: any) => {
    if (!currentUserId || !rfq.invitations) return false;
    return rfq.invitations.some(
      (inv: any) => inv.vendor_user_id === currentUserId && 
      inv.invitation_status !== 'expired'
    );
  };

  // Sort RFQs to show invited ones first
  const sortedRfqs = [...rfqs].sort((a, b) => {
    const aInvited = isUserInvited(a);
    const bInvited = isUserInvited(b);
    if (aInvited && !bInvited) return -1;
    if (!aInvited && bInvited) return 1;
    return 0;
  });

  if (error) {
    return (
      <div className="container mx-auto p-6">
        <div className="text-center text-red-600">
          Error loading RFQs: {error}
        </div>
      </div>
    );
  }

  return (
    <TooltipProvider>
    <div className="container mx-auto p-4 sm:p-6">
      <div className="mb-6">
        <h1 className="text-2xl sm:text-3xl font-bold mb-2">Browse RFQs</h1>
        <p className="text-sm sm:text-base text-muted-foreground">
          Discover procurement opportunities that match your capabilities.
        </p>
      </div>

      {/* Verification Banner for Unverified Vendors */}
      {showRestricted && !verificationLoading && (
        <Alert className="mb-6 border-orange-200 bg-orange-50">
          <AlertTriangle className="h-4 w-4 text-orange-600" />
          <AlertDescription className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3">
            <span className="text-orange-800">
              <strong>Verification Required:</strong> Your profile must be verified to view RFQ details and submit bids. 
              Some information is hidden until you're verified.
            </span>
            <Button asChild size="sm" variant="outline" className="w-fit border-orange-300 hover:bg-orange-100">
              <Link to="/profile/vendor">Request Verification</Link>
            </Button>
          </AlertDescription>
        </Alert>
      )}

      {/* Search and Filters */}
      <Card className="mb-6">
        <CardContent className="p-4">
          <div className="flex flex-col sm:flex-row gap-4">
            <div className="flex-1 relative">
              <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-muted-foreground h-4 w-4" />
              <Input
                placeholder="Search RFQs..."
                value={search}
                onChange={(e) => setSearch(e.target.value)}
                className="pl-9"
              />
            </div>
            <Select value={selectedCategory || 'all'} onValueChange={(value) => setSelectedCategory(value === 'all' ? '' : value as JobCategory)}>
              <SelectTrigger className="w-full sm:w-64">
                <SelectValue placeholder="All Categories" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">All Categories</SelectItem>
                {JOB_CATEGORIES.map((category) => (
                  <SelectItem key={category.value} value={category.value}>
                    {category.label}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
        </CardContent>
      </Card>

      {/* RFQ Cards */}
      {loading ? (
        <div className="text-center py-8">Loading RFQs...</div>
      ) : rfqs.length === 0 ? (
        <Card className="p-8 text-center">
          <h3 className="text-lg font-medium mb-2">No RFQs found</h3>
          <p className="text-muted-foreground">
            No active RFQs match your current search criteria.
          </p>
        </Card>
      ) : (
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
          {sortedRfqs.map((rfq: any) => {
            const matchScore = matchScores[rfq.id];
            const categoryLabel = rfq.job ? JOB_CATEGORIES.find(c => c.value === rfq.job.category)?.label : 'Unknown';
            const invited = isUserInvited(rfq);
            
            // Determine display values based on verification status
            const displayTitle = showRestricted ? 'RFQ Name Hidden' : (rfq.job?.title || 'RFQ Title');
            const displayOrgName = showRestricted ? getObscuredOrgName(rfq) : (rfq.job?.organization?.name || 'Client Organization');
            const displayDescription = showRestricted ? 'Verify your profile to view details' : (rfq.job?.description || 'No description available');
            
            return (
              <Card 
                key={rfq.id} 
                className={`hover:shadow-lg transition-shadow ${
                  invited ? 'border-2 border-primary bg-primary/5' : ''
                }`}
              >
                <CardHeader className="p-4 pb-2">
                  {invited && (
                    <Badge className="mb-2 w-fit bg-primary text-primary-foreground">
                      <Mail className="h-3 w-3 mr-1" />
                      Private Invitation
                    </Badge>
                  )}
                  <div className="flex justify-between items-start gap-4">
                    <div className="flex-1 min-w-0">
                      <div className="flex items-center gap-2 mb-1">
                        <CardTitle className={`text-lg truncate ${showRestricted ? 'text-muted-foreground italic' : ''}`}>
                          {displayTitle}
                        </CardTitle>
                        {invited && <Mail className="h-4 w-4 text-primary flex-shrink-0" />}
                      </div>
                      <p className={`text-sm ${showRestricted ? 'text-muted-foreground/70 italic' : 'text-muted-foreground'}`}>
                        {displayOrgName} • {categoryLabel}
                      </p>
                    </div>
                    <div className="flex flex-col items-end gap-1 flex-shrink-0">
                      <div className="flex items-center gap-2">
                        {matchScore ? (
                          <MatchScoreDisplay matchScore={matchScore} compact={true} />
                        ) : rfq.rfq_targeting_criteria ? (
                          <div className="flex items-center gap-1">
                            <Star className="h-4 w-4 text-muted-foreground" />
                            <span className="text-xs text-muted-foreground">Loading...</span>
                          </div>
                        ) : (
                          <Badge variant="secondary" className="text-xs">No Criteria</Badge>
                        )}
                        {(() => {
                          const isExpired = new Date(rfq.closes_at).getTime() <= new Date().getTime();
                          let displayStatus: string = rfq.status;
                          if (isExpired) {
                            displayStatus = 'closed';
                          }
                          return (
                            <Badge variant={displayStatus === 'open' ? 'default' : 'secondary'}>
                              {displayStatus}
                            </Badge>
                          );
                        })()}
                      </div>
                      <div className="flex items-center gap-1 text-sm text-orange-600">
                        <Clock className="h-3 w-3" />
                        <span className="text-xs font-medium">{getTimeRemaining(rfq.closes_at)}</span>
                      </div>
                    </div>
                  </div>
                </CardHeader>
                
                <CardContent className="p-4 pt-0">
                  <p className={`mb-2 line-clamp-1 text-sm ${showRestricted ? 'text-muted-foreground/70 italic' : 'text-muted-foreground'}`}>
                    {displayDescription}
                  </p>
                  
                  {/* Combined Products and Match Criteria Row */}
                  {(rfq.job?.specifications && rfq.job.specifications.length > 0) || (matchScore && !matchScore.details.no_criteria) ? (
                    <div className="flex flex-wrap items-center gap-1.5 mb-2">
                      {/* Products */}
                      {rfq.job?.specifications && rfq.job.specifications.length > 0 && (
                        <>
                          {rfq.job.specifications.slice(0, 3).map((spec: any, idx: number) => (
                            <Badge 
                              key={idx} 
                              variant="secondary" 
                              className={`text-xs ${showRestricted ? 'italic text-muted-foreground/70' : ''}`}
                            >
                              {showRestricted ? 'Product Hidden' : spec.specification_name}
                            </Badge>
                          ))}
                          {rfq.job.specifications.length > 3 && (
                            <Badge variant="secondary" className="text-xs">
                              +{rfq.job.specifications.length - 3} more
                            </Badge>
                          )}
                        </>
                      )}
                      
                      {/* Separator */}
                      {rfq.job?.specifications && rfq.job.specifications.length > 0 && matchScore && !matchScore.details.no_criteria && (
                        <span className="text-muted-foreground mx-1">•</span>
                      )}
                      
                      {/* Match Criteria - Compact icons only */}
                      {matchScore && !matchScore.details.no_criteria && (
                        <>
                          {matchScore.details.certifications_met && (
                            <Badge variant="outline" className="text-xs px-1.5" title="Certifications met">
                              <Award className="h-3 w-3" />
                            </Badge>
                          )}
                          {matchScore.details.rating_met && (
                            <Badge variant="outline" className="text-xs px-1.5" title="Rating requirement met">
                              <Star className="h-3 w-3" />
                            </Badge>
                          )}
                          {matchScore.details.jobs_met && (
                            <Badge variant="outline" className="text-xs px-1.5" title="Experience requirement met">
                              <Briefcase className="h-3 w-3" />
                            </Badge>
                          )}
                          {matchScore.details.verification_met && (
                            <Badge variant="outline" className="text-xs px-1.5" title="Verified vendor">
                              <ShieldCheck className="h-3 w-3" />
                            </Badge>
                          )}
                        </>
                      )}
                    </div>
                  ) : null}

                  {/* Streamlined Footer */}
                  <div className="flex justify-between items-center pt-2 border-t">
                    <div className="text-xs text-muted-foreground flex items-center gap-3">
                      <span>Rev. {rfq.revision}</span>
                      <span>{rfq.questions?.length || 0} question{rfq.questions?.length === 1 ? '' : 's'}</span>
                      {rfq.job?.delivery_details?.[0]?.deadline_type && (
                        <DeadlineTypeBadge type={rfq.job.delivery_details[0].deadline_type as DeadlineType} showIcon={false} />
                      )}
                    </div>
                    {showRestricted ? (
                      <Tooltip>
                        <TooltipTrigger asChild>
                          <Button asChild size="sm" variant="outline">
                            <Link to="/profile/vendor">Verify to View</Link>
                          </Button>
                        </TooltipTrigger>
                        <TooltipContent>
                          <p>Complete verification to view RFQ details and submit bids</p>
                        </TooltipContent>
                      </Tooltip>
                    ) : (
                      <Link to={`/rfqs/${rfq.public_id}`}>
                        <Button size="sm">View RFQ</Button>
                      </Link>
                    )}
                  </div>
                </CardContent>
              </Card>
            );
          })}
        </div>
      )}
    </div>
    </TooltipProvider>
  );
}